"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import Cookies from "js-cookie";

export type ChecklistState = Record<string, boolean>;

/**
 * 체크리스트 완료 상태를 쿠키에 영속하며, **같은 쿠키를 공유하는 여러 인스턴스 간 양방향 동기화**를
 * 제공하는 훅 (JCG-2101).
 *
 * jeongcheogi-application 은 본문 표(ChecklistTable)와 좌측 사이드바(ChecklistMini)가 동일
 * cookieName 을 공유하므로, 한쪽에서 체크하면 다른 쪽도 즉시 반영돼야 한다. 쿠키는 storage 이벤트를
 * 발생시키지 않으므로 toggle 시 커스텀 이벤트(`SYNC_EVENT`)를 브로드캐스트하고 **다른** 인스턴스가 이를
 * 수신해 상태를 맞춘다(같은 cookieName, 발신 인스턴스 자신은 origin 으로 제외). 단일 인스턴스
 * 페이지(pre-application 등)에서는 수신 대상이 없어 동작 변화가 없다.
 */
const SYNC_EVENT = "jcg-checklist-sync";

interface SyncDetail {
  cookieName: string;
  state: ChecklistState;
  /** 발신 훅 인스턴스 식별자 — 자기가 보낸 이벤트를 되돌려 받아 중복 setState 하지 않도록. */
  origin: object;
}

export function useChecklist(cookieName: string) {
  const [checkedState, setCheckedState] = useState<ChecklistState>({});

  // 최신 state 미러 — toggle 이 stale 없이 next 를 계산하면서도 deps 를 [cookieName] 으로 유지(콜백
  // 안정화)하기 위함. 쿠키 복원·sync 수신·toggle 모든 경로의 변경을 effect 가 반영한다.
  const stateRef = useRef(checkedState);
  useEffect(() => {
    stateRef.current = checkedState;
  }, [checkedState]);
  // 이 훅 인스턴스의 고유 식별자(자기 발신 이벤트 무시용).
  const selfId = useRef<object>({});

  // 쿠키 복원 (마운트 후)
  useEffect(() => {
    const saved = Cookies.get(cookieName);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (typeof parsed === "object" && parsed !== null) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setCheckedState(parsed as ChecklistState);
        }
      } catch (error) {
        console.error("Failed to parse checklist cookie:", error);
      }
    }
  }, [cookieName]);

  // 다른 인스턴스(본문 ↔ 사이드바)의 변경을 수신해 동기화 (같은 cookieName, 자기 발신 제외)
  useEffect(() => {
    const onSync = (e: Event) => {
      const detail = (e as CustomEvent<SyncDetail>).detail;
      if (detail?.cookieName === cookieName && detail.origin !== selfId.current) {
        setCheckedState(detail.state);
      }
    };
    window.addEventListener(SYNC_EVENT, onSync);
    return () => window.removeEventListener(SYNC_EVENT, onSync);
  }, [cookieName]);

  const toggle = useCallback(
    (id: string) => {
      const next = { ...stateRef.current, [id]: !stateRef.current[id] };
      stateRef.current = next; // 같은 tick 내 연속 toggle 도 최신값 기준이 되도록 즉시 반영
      setCheckedState(next);
      Cookies.set(cookieName, JSON.stringify(next), { expires: 365 });
      window.dispatchEvent(
        new CustomEvent<SyncDetail>(SYNC_EVENT, {
          detail: { cookieName, state: next, origin: selfId.current },
        }),
      );
    },
    [cookieName],
  );

  return { checkedState, toggle };
}
